import { z } from 'zod';
import { runUsageSchema } from './agents';
import { gatewayAttachmentSchema, MAX_ATTACHMENTS_PER_MESSAGE } from './files';
import { isoTimestamp } from './common';
import { mappingDocSchema, sdkOptionSchema } from './mapping';
import { providerTypeSchema } from './providers';

// --- API keys ---

const keyNameSchema = z.string().trim().min(1, 'Name is required').max(100);
export const apiKeyMetadataSchema = z
  .record(z.string(), z.unknown())
  .refine((m) => JSON.stringify(m).length <= 8192, { message: 'Metadata is limited to 8 KiB' });

export const apiKeyDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  prefix: z.string(),
  allAgents: z.boolean(),
  agentIds: z.array(z.uuid()),
  metadata: z.record(z.string(), z.unknown()),
  expiresAt: isoTimestamp.nullable(),
  revokedAt: isoTimestamp.nullable(),
  lastUsedAt: isoTimestamp.nullable(),
  createdAt: isoTimestamp,
});
export type ApiKeyDto = z.infer<typeof apiKeyDtoSchema>;

const scopeRefine = (v: { allAgents?: boolean; agentIds?: string[] }) =>
  v.allAgents !== false || (v.agentIds?.length ?? 0) > 0;
const scopeMessage = {
  message: 'Choose at least one agent, or allow all agents',
  path: ['agentIds'],
};

export const createApiKeyRequestSchema = z
  .object({
    name: keyNameSchema,
    allAgents: z.boolean().default(true),
    agentIds: z.array(z.uuid()).max(500).default([]),
    metadata: apiKeyMetadataSchema.default({}),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
  })
  .refine(scopeRefine, scopeMessage);
export type CreateApiKeyRequest = z.infer<typeof createApiKeyRequestSchema>;

export const updateApiKeyRequestSchema = z
  .object({
    name: keyNameSchema.optional(),
    allAgents: z.boolean().optional(),
    agentIds: z.array(z.uuid()).max(500).optional(),
    metadata: apiKeyMetadataSchema.optional(),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateApiKeyRequest = z.infer<typeof updateApiKeyRequestSchema>;

export const createdApiKeySchema = z.object({
  apiKey: apiKeyDtoSchema,
  /** The full key. Shown once; only its hash is stored. */
  key: z.string(),
});
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;

// --- Endpoints ---

export const rateLimitSettingsSchema = z
  .object({
    perKeyPerMinute: z.number().int().min(1).max(10_000),
    perAgentPerMinute: z.number().int().min(1).max(100_000),
  })
  .strict();
export type RateLimitSettings = z.infer<typeof rateLimitSettingsSchema>;
export const DEFAULT_RATE_LIMIT: RateLimitSettings = {
  perKeyPerMinute: 60,
  perAgentPerMinute: 600,
};

/** Headers that carry credentials and so never identify a conversation's user or scope. */
const FORBIDDEN_HISTORY_HEADERS = ['authorization', 'x-api-key', 'cookie', 'proxy-authorization'];
const historyHeaderSchema = z
  .string()
  .regex(/^[a-z0-9-]{1,64}$/, 'Use a lower-case header name (letters, digits and -)')
  .refine((h) => !FORBIDDEN_HISTORY_HEADERS.includes(h), {
    message: 'This header carries credentials and cannot be used',
  });

const historyFields = {
  enabled: z.boolean(),
  userHeader: historyHeaderSchema,
  /** `null` keeps one history per user, without scopes. */
  scopeHeader: historyHeaderSchema.nullable(),
  /** Stored messages sent to the model with each request. */
  maxMessages: z.number().int().min(1).max(200),
  /** Approximate token budget for that history (characters / 4). */
  maxTokens: z.number().int().min(1000).max(200_000),
  autoTitle: z.boolean(),
  /** Keep a running summary of the messages that fell out of the history window. */
  summarize: z.boolean(),
  /** Size budget of that summary (characters / 4). */
  summaryMaxTokens: z.number().int().min(200).max(4000),
};

/** Header-keyed conversation history kept by the gateway for an endpoint. */
export const conversationHistorySettingsSchema = z
  .object({
    enabled: historyFields.enabled.default(false),
    userHeader: historyFields.userHeader.default('x-user-id'),
    scopeHeader: historyFields.scopeHeader.default('x-conversation-scope'),
    maxMessages: historyFields.maxMessages.default(40),
    maxTokens: historyFields.maxTokens.default(30_000),
    autoTitle: historyFields.autoTitle.default(true),
    summarize: historyFields.summarize.default(false),
    summaryMaxTokens: historyFields.summaryMaxTokens.default(1000),
  })
  .strict()
  .refine((v) => v.userHeader !== v.scopeHeader, {
    message: 'The user and scope headers must differ',
    path: ['scopeHeader'],
  });

/**
 * A partial conversation-history update: no defaults, so omitted fields keep their stored
 * values. The endpoint service merges it over the stored settings and validates the result.
 */
export const conversationHistoryPatchSchema = z.object(historyFields).partial().strict();
export type ConversationHistoryPatch = z.infer<typeof conversationHistoryPatchSchema>;
export type ConversationHistorySettings = z.infer<typeof conversationHistorySettingsSchema>;
export const DEFAULT_CONVERSATION_HISTORY: ConversationHistorySettings = {
  enabled: false,
  userHeader: 'x-user-id',
  scopeHeader: 'x-conversation-scope',
  maxMessages: 40,
  maxTokens: 30000,
  autoTitle: true,
  summarize: false,
  summaryMaxTokens: 1000,
};

export const originSchema = z
  .string()
  .trim()
  .regex(/^https?:\/\/[A-Za-z0-9.-]+(:\d{1,5})?$/, 'Use an origin like https://app.example.com');

export const endpointDtoSchema = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  agentName: z.string(),
  agentSlug: z.string(),
  publicId: z.string(),
  url: z.string(),
  enabled: z.boolean(),
  streamingEnabled: z.boolean(),
  attachmentsEnabled: z.boolean(),
  rateLimit: rateLimitSettingsSchema,
  conversationHistory: conversationHistorySettingsSchema,
  allowedOrigins: z.array(z.string()),
  allowedSdkOverrides: z.array(sdkOptionSchema),
  mapping: mappingDocSchema,
  mappingVersion: z.number().int(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type EndpointDto = z.infer<typeof endpointDtoSchema>;

export const createEndpointRequestSchema = z.object({ agentId: z.uuid() });

export const updateEndpointRequestSchema = z
  .object({
    enabled: z.boolean().optional(),
    streamingEnabled: z.boolean().optional(),
    attachmentsEnabled: z.boolean().optional(),
    rateLimit: rateLimitSettingsSchema.optional(),
    conversationHistory: conversationHistoryPatchSchema.optional(),
    allowedOrigins: z.array(originSchema).max(20).optional(),
    allowedSdkOverrides: z.array(sdkOptionSchema).max(5).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateEndpointRequest = z.infer<typeof updateEndpointRequestSchema>;

export const saveMappingRequestSchema = z.object({ mapping: mappingDocSchema });

/** A sample request for the mapping dry run. */
export const dryRunRequestSchema = z.object({
  headers: z.record(z.string(), z.string()).default({}),
  query: z.record(z.string(), z.string()).default({}),
  body: z.unknown().default({}),
  apiKeyMetadata: z.record(z.string(), z.unknown()).default({}),
  /** Dry-run an unsaved document; defaults to the saved mapping. */
  mapping: mappingDocSchema.optional(),
});
export type DryRunRequest = z.infer<typeof dryRunRequestSchema>;

export const dryRunResponseSchema = z.object({
  ok: z.boolean(),
  errors: z.array(z.object({ ruleId: z.string(), code: z.string(), message: z.string() })),
  resolved: z.object({
    input: z.unknown().nullable(),
    promptVars: z.record(z.string(), z.string()),
    sdk: z.record(z.string(), z.unknown()),
    toolVars: z.record(z.string(), z.record(z.string(), z.unknown())),
    mcp: z.record(z.string(), z.record(z.string(), z.unknown())),
    env: z.record(z.string(), z.string()),
  }),
  /** The system prompt after prompt variables are applied. */
  systemPrompt: z.string(),
});
export type DryRunResponse = z.infer<typeof dryRunResponseSchema>;

// --- Gateway (client-facing) ---

/** A gateway message: like a chat message, but files are uploaded ids or inline base64. */
export const gatewayMessageSchema = z
  .object({
    role: z.enum(['user', 'assistant']),
    content: z.string().max(100_000).default(''),
    attachments: z.array(gatewayAttachmentSchema).max(MAX_ATTACHMENTS_PER_MESSAGE).optional(),
  })
  .refine((m) => m.content.trim().length > 0 || (m.attachments?.length ?? 0) > 0, {
    message: 'Write a message or attach a file',
    path: ['content'],
  })
  .refine((m) => m.role === 'user' || !m.attachments?.length, {
    message: 'Only user messages carry attachments',
    path: ['attachments'],
  });
export type GatewayMessage = z.infer<typeof gatewayMessageSchema>;

export const gatewayRequestSchema = z
  .object({
    input: z.union([z.string().min(1).max(100_000), z.array(gatewayMessageSchema).min(1).max(200)]),
    threadId: z.string().min(1).max(200).optional(),
    /** Continues a stored conversation (endpoints with conversation history). */
    conversationId: z.uuid().optional(),
    stream: z.boolean().optional(),
  })
  .passthrough();
export type GatewayRequest = z.infer<typeof gatewayRequestSchema>;

export const gatewayResponseSchema = z.object({
  runId: z.uuid(),
  output: z.object({ text: z.string() }),
  usage: runUsageSchema,
  threadId: z.string().optional(),
});
export type GatewayResponse = z.infer<typeof gatewayResponseSchema>;

export const gatewayDescribeSchema = z.object({
  name: z.string(),
  description: z.string(),
  streaming: z.boolean(),
  input: z.object({
    accepts: z.array(z.string()),
    body: z.record(z.string(), z.string()),
  }),
});

// --- Endpoint conversations ---

export const conversationSummarySchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  messageCount: z.number().int(),
  lastActiveAt: z.string(),
  createdAt: z.string(),
});
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

export const conversationMessageSchema = z.object({
  id: z.uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  createdAt: z.string(),
});
export type ConversationMessage = z.infer<typeof conversationMessageSchema>;

export const conversationListResponseSchema = z.object({
  items: z.array(conversationSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;

export const conversationMessagesResponseSchema = z.object({
  items: z.array(conversationMessageSchema),
});
export type ConversationMessagesResponse = z.infer<typeof conversationMessagesResponseSchema>;

export const renameConversationRequestSchema = z.object({
  title: z.string().trim().min(1).max(120),
});
export type RenameConversationRequest = z.infer<typeof renameConversationRequestSchema>;

/** Body of POST …/conversations/:id/move: the scope key the conversation moves to. */
export const moveConversationRequestSchema = z.object({
  scope: z.string().trim().min(1).max(200),
});
export type MoveConversationRequest = z.infer<typeof moveConversationRequestSchema>;

// --- Endpoint conversations (console) ---

/** `GET /api/admin/endpoints/:id/conversations` filters. */
export const adminConversationListQuerySchema = z.object({
  /** Exact user key. */
  userKey: z.string().max(200).optional(),
  /** Exact scope key; empty matches conversations without a scope. */
  scopeKey: z.string().max(200).optional(),
  /** Case-insensitive substring of the title. */
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  cursor: z.uuid().optional(),
});
export type AdminConversationListQuery = z.infer<typeof adminConversationListQuerySchema>;

export const adminConversationSummarySchema = z.object({
  id: z.uuid(),
  userKey: z.string(),
  /** `''` when the conversation has no scope. */
  scopeKey: z.string(),
  title: z.string().nullable(),
  messageCount: z.number().int(),
  approxTokens: z.number().int(),
  /** Total cost of the priced runs. */
  costUsd: z.number(),
  /** Runs whose model had no known price, so they are missing from `costUsd`. */
  unpricedRuns: z.number().int(),
  lastActiveAt: isoTimestamp,
  createdAt: isoTimestamp,
});
export type AdminConversationSummary = z.infer<typeof adminConversationSummarySchema>;

export const adminConversationListResponseSchema = z.object({
  items: z.array(adminConversationSummarySchema),
  nextCursor: z.string().nullable(),
});
export type AdminConversationListResponse = z.infer<typeof adminConversationListResponseSchema>;

export const adminConversationMessageSchema = z.object({
  id: z.uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  runId: z.uuid().nullable(),
  /** Cost of the run behind the message; null when unknown. */
  costUsd: z.number().nullable(),
  createdAt: isoTimestamp,
});
export type AdminConversationMessage = z.infer<typeof adminConversationMessageSchema>;

export const adminConversationDetailSchema = adminConversationSummarySchema.extend({
  /** Oldest first. */
  messages: z.array(adminConversationMessageSchema),
});
export type AdminConversationDetail = z.infer<typeof adminConversationDetailSchema>;

// --- Usage ---

export const usageGroupBySchema = z.enum(['day', 'agent', 'apiKey', 'model']);
export type UsageGroupBy = z.infer<typeof usageGroupBySchema>;
export const usageQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  groupBy: usageGroupBySchema.default('day'),
});
export type UsageQuery = z.infer<typeof usageQuerySchema>;
export const usageRowSchema = z.object({
  key: z.string().nullable(),
  label: z.string().nullable(),
  runs: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costUsd: z.number(),
  unpricedRuns: z.number().int(),
  cacheReadTokens: z.number().int(),
  cacheWriteTokens: z.number().int(),
});
export type UsageRow = z.infer<typeof usageRowSchema>;

// --- Prompt caching report ---

export const cacheReportQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});
export type CacheReportQuery = z.infer<typeof cacheReportQuerySchema>;

const cacheCountsSchema = z.object({
  runs: z.number().int(),
  runsWithHit: z.number().int(),
  inputTokens: z.number().int(),
  cacheReadTokens: z.number().int(),
  cacheWriteTokens: z.number().int(),
});
export const cacheReportSchema = z.object({
  totals: cacheCountsSchema.extend({ costUsd: z.number() }),
  daily: z.array(cacheCountsSchema.extend({ day: z.string() })),
  models: z.array(
    cacheCountsSchema.extend({ providerType: providerTypeSchema, model: z.string() }),
  ),
  recent: z.array(
    z.object({
      id: z.uuid(),
      startedAt: z.iso.datetime({ offset: true }),
      agentId: z.uuid(),
      agentName: z.string().nullable(),
      providerType: providerTypeSchema,
      model: z.string(),
      inputTokens: z.number().int(),
      cacheReadTokens: z.number().int(),
      cacheWriteTokens: z.number().int(),
      costUsd: z.number().nullable(),
    }),
  ),
});
export type CacheReport = z.infer<typeof cacheReportSchema>;
